import React, { useCallback, useMemo } from "react";
import { Star } from "lucide-react";
import { Button } from "../ui/button";
import { Product } from "@nusrif/types";

type ProductInfoProduct = Pick<
  Product,
  | "id"
  | "name"
  | "price"
  | "rating"
  | "reviewCount"
  | "availability"
  | "sizes"
  | "colors"
  | "variants"
>;

interface ProductInfoProps {
  product: ProductInfoProduct;
  selectedSize: string;
  selectedColor: string;
  onSizeChange: (size: string) => void;
  onColorChange: (color: string) => void;
}

export const ProductInfo = React.memo(({
  product,
  selectedSize,
  selectedColor,
  onSizeChange,
  onColorChange,
}: ProductInfoProps) => {
  const renderStars = useCallback((rating: number) => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star
        key={index}
        className={`w-4 h-4 ${
          index < rating
            ? "fill-secondary-yellow text-secondary-yellow"
            : "text-gray-300"
        }`}
      />
    ));
  }, []);

  const handleColorClick = useCallback((color: string) => {
    onColorChange(color);
  }, [onColorChange]);

  // Get stock status for each size option
  const sizeStockStatus = useMemo(() => {
    return product.sizes.map(size => {
      const variant = product.variants.find(v => 
        v.options.some(opt => opt.name === size)
      );
      
      if (!variant) {
        return { size, isInStock: false, stockLevel: 0, stockStatus: 'out_of_stock' as const };
      }
      
      const stockLevel = variant.stockLevels.reduce((sum, level) => 
        sum + level.stockOnHand - level.stockAllocated, 0
      );
      const isInStock = stockLevel > variant.outOfStockThreshold;
      
      let stockStatusType: 'in_stock' | 'low_stock' | 'out_of_stock';
      if (stockLevel <= variant.outOfStockThreshold) {
        stockStatusType = 'out_of_stock';
      } else if (stockLevel <= 5) {
        stockStatusType = 'low_stock';
      } else {
        stockStatusType = 'in_stock';
      }
      
      return { size, isInStock, stockLevel, stockStatus: stockStatusType };
    });
  }, [product]);

  const stars = useMemo(() => renderStars(product.rating), [renderStars, product.rating]);



  const getStockIndicator = (stockStatus: 'in_stock' | 'low_stock' | 'out_of_stock', stockLevel: number) => {
    switch (stockStatus) {
      case 'out_of_stock':
        return <span className="text-xs text-gray-400">Out of stock</span>;
      case 'low_stock':
        return <span className="text-xs text-orange-600">Only {stockLevel} left</span>;
      case 'in_stock':
        return null; // Don't show "In stock" text as it's clear from the visual indicators
    }
  };

  return (
    <div className="space-y-6">
      {/* Product Title and Rating */}
      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-black">{product.name}</h1>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            {stars}
          </div>
          <span className="text-sm text-gray-600">
            ({product.reviewCount} Review)
          </span>
        </div>
      </div>
      
      {/* Availability */}
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 bg-success rounded-full"></div>
        <span className="text-sm text-gray-600">{product.availability}</span>
      </div>
      
      {/* Size Selection */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-black">Size</h3>
        <div className="flex gap-3">
          {sizeStockStatus.map(({ size, stockLevel, stockStatus }) => (
            <div key={size} className="flex flex-col items-center gap-1">
              <Button
                variant={selectedSize === size ? "default" : "outline"}
                className={`w-12 h-12 rounded-full text-sm font-medium transition-all duration-200 ${
                  selectedSize === size
                    ? ""
                    : stockStatus === 'out_of_stock'
                    ? 'border-gray-200 text-gray-400 cursor-not-allowed opacity-50'
                    : stockStatus === 'low_stock'
                    ? 'border-orange-300 text-orange-700 hover:border-orange-400 hover:bg-orange-50'
                    : 'border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                }`}
                onClick={() => onSizeChange(size)}
                disabled={stockStatus === 'out_of_stock'}
              >
                {size}
              </Button>
              {getStockIndicator(stockStatus, stockLevel)}
            </div>
          ))}
        </div>
      </div>
      
      {/* Color Selection */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold text-black">Color</h3>
        <div className="flex gap-3">
          {product.colors.map((color) => (
            <button
              key={color.name}
              className={`w-8 h-8 rounded-full border-2 transition-all duration-200 ${
                selectedColor === color.name
                  ? "border-gray-800 scale-110"
                  : "border-gray-300 hover:border-gray-400"
              }`}
              style={{ backgroundColor: color.value }}
              onClick={() => handleColorClick(color.name)}
              title={color.name}
            />
          ))}
        </div>
      </div>
    </div>
  );
});

ProductInfo.displayName = "ProductInfo";
